// src/pages/B2B/crm/components/MonthGrid.tsx
import React from 'react';
import { Meeting } from '../api/types';

type Props = {
  monthAnchor: Date;                          // any date inside the visible month
  meetings: Meeting[];
  onDayClick: (day: Date) => void;
  onEventClick: (m: Meeting) => void;
};

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function startOfMonthGrid(anchor: Date): Date {
  // Sunday-aligned (per spec): roll back to the most recent Sunday on or before the 1st.
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const dayOfWeek = first.getDay();  // 0=Sun..6=Sat
  const start = new Date(first);
  start.setDate(first.getDate() - dayOfWeek);
  return start;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export function MonthGrid({ monthAnchor, meetings, onDayClick, onEventClick }: Props): React.ReactElement {
  const start = startOfMonthGrid(monthAnchor);
  const today = new Date();
  const cells: Date[] = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });

  // Bucket meetings by local YYYY-MM-DD of starts_at.
  const byDay = new Map<string, Meeting[]>();
  for (const m of meetings) {
    const key = new Date(m.starts_at).toLocaleDateString('en-CA');  // YYYY-MM-DD
    const arr = byDay.get(key) ?? [];
    arr.push(m);
    byDay.set(key, arr);
  }

  return (
    <div className="border border-[#2a2a3e] rounded-lg overflow-hidden">
      <div className="grid grid-cols-7 bg-[#1a1a2e] border-b border-[#2a2a3e]">
        {DAY_LABELS.map(d => (
          <div key={d} className="text-violet-400 text-xs font-semibold text-center py-2">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 grid-rows-6">
        {cells.map((d, i) => {
          const inMonth = d.getMonth() === monthAnchor.getMonth();
          const isToday = sameDay(d, today);
          const key = d.toLocaleDateString('en-CA');
          const dayMeetings = byDay.get(key) ?? [];
          const visible = dayMeetings.slice(0, 3);
          const overflow = dayMeetings.length - visible.length;

          return (
            <button
              key={i}
              type="button"
              onClick={() => onDayClick(d)}
              className={[
                'text-left border-r border-b border-[#2a2a3e] min-h-[96px] p-1.5 align-top',
                inMonth ? 'bg-[#0f0f1a]' : 'bg-[#14141f] opacity-60',
              ].join(' ')}
            >
              <div className={`text-xs ${isToday ? 'text-violet-300 font-bold' : 'text-gray-400'}`}>
                {d.getDate()}
              </div>
              <div className="space-y-0.5 mt-1">
                {visible.map(m => (
                  <div
                    key={m.id}
                    role="button"
                    onClick={(e) => { e.stopPropagation(); onEventClick(m); }}
                    className="bg-violet-700/30 border border-violet-600/40 text-violet-100 text-[11px] rounded px-1 py-0.5 truncate hover:bg-violet-700/50"
                    title={`${m.all_day ? 'All day' : fmtTime(m.starts_at)} — ${m.title}`}
                  >
                    {m.all_day ? '' : `${fmtTime(m.starts_at)} · `}{m.title}
                  </div>
                ))}
                {overflow > 0 && (
                  <div className="text-[11px] text-gray-500">+{overflow} more</div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
